import { apiFetch } from './api';

/**
 * Token API utilities for managing Personal Access Tokens
 */

/**
 * Get all available scopes with descriptions
 * @returns {Promise<Array>} Array of scope objects with scope and description
 */
export const getAvailableScopes = async () => {
  const response = await apiFetch('/api/tokens/scopes');
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch scopes');
  }
  const data = await response.json();
  return data.scopes;
};

/**
 * Create a new personal access token
 * @param {string} name - Token name
 * @param {Array<string>} scopes - Array of scope strings
 * @param {string|null} expiresAt - ISO 8601 date string or null for 1 year default
 * @returns {Promise<Object>} Token data including the plain token (only shown once!)
 */
export const createToken = async (name, scopes, expiresAt = null) => {
  const response = await apiFetch('/api/tokens', {
    method: 'POST',
    body: JSON.stringify({
      name,
      scopes,
      expires_at: expiresAt,
    }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create token');
  }
  return await response.json();
};

/**
 * Get all personal access tokens for the authenticated user
 * @returns {Promise<Array>} Array of token objects (without plain token values)
 */
export const getTokens = async () => {
  const response = await apiFetch('/api/tokens');
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch tokens');
  }
  const data = await response.json();
  return data.tokens;
};

/**
 * Delete a personal access token
 * @param {string} tokenId - UUID of the token to delete
 * @returns {Promise<Object>} Success message
 */
export const deleteToken = async (tokenId) => {
  const response = await apiFetch(`/api/tokens/${tokenId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete token');
  }
  return await response.json();
};
